import React, {useContext, useEffect, useMemo, useState} from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import LocationListFilter, {ILocationListCheckboxFilter} from "./LocationListFilter";
import LocationListRecords from "./LocationListRecords";
import withAppContext from "tbk-components/src/hooks/withAppContext";
import useHashAsState from 'tbk-components/src/hooks/useHashAsState';
import Loading from "tbk-components/src/components/Loading/index";
import LocationListPagination from "./LocationListPagination";
import DonationForm from "../DonorTool/DonorToolComponents/DonationForm";

export interface ILocationListProps extends IBaseElementProps {
	regionID?: number;
	image?: IImage;
	heading?: string;
	subheading?: string;
	description?: string;
	ministryBrochureLink?: string;
	ministryDescriptions?: any[];
	linksHeading?: string;
	sideBarList?: string[];
	intro?: string;
	programCategories?: any;
	byProgramCodeURL?: string;
	filters?: any;
}

const LocationList: React.FC<ILocationListProps> = withAppContext( ({
	context,
	regionID,
	image,
	heading,
	description,
	ministryBrochureLink,
	ministryDescriptions,
	subheading,
	linksHeading,
	sideBarList,
	intro,
	programCategories,
	byProgramCodeURL,
	filters,
}) => {
	const {Api} = useContext(context);
	const [ busy, setBusy ] = useState( false );
	const [ totalPages, setTotalPages ] = useState( 0 );
	const [ records, setRecords ] = useState();
	const [ donationFormProps, setDonationFormProps ] = useState( [] );

	const dynamicParameters = {};
	(filters?.selects).forEach( select => {dynamicParameters[ select.id ] = 'string'} );

	const [ state, normalizedState, updateState ] = useHashAsState({
		parameters: {
			currentPage : 'number',
			programCategories: 'csv',
			...dynamicParameters,
		},
		defaults: {
			currentPage : 1,
		},
	});

	const updateStateAndResetPage = change => {
		updateState({...change,currentPage:1});
	};

	const handleCheckboxSelection = checkboxSelection => {
		const selectedProgramCategories = [...( state.programCategories || [] )];
		//add or subtract selection from filter
		let index = selectedProgramCategories.indexOf( checkboxSelection.toString(), 0 );
		if ( index > -1 ) {
			selectedProgramCategories.splice(index, 1);
		} else {
			selectedProgramCategories.push(checkboxSelection);
		}

		updateStateAndResetPage( { programCategories: selectedProgramCategories })
	}

	const handleFilterSelection = (filterName, filterSelection ) => {
		updateStateAndResetPage( { [filterName]: filterSelection } );
	}

	useEffect( () => {
		setBusy(true);
		Api.getDonationFormProps( [ regionID, state ] ).then( results => {
			setRecords(results.records);
			setTotalPages(results.totalPages);
			setDonationFormProps(results.donationFormProps);
		}).finally( () => { setBusy( false ); });
	}, [normalizedState]);

	const filteredMinistries = useMemo( () => {
		let selectOptions = [];
		 if ( state.nation_select == null ) {
			return null;
		}

		filters?.selects.map( (select, index ) => {
			if ( select?.id == 'ministry_select' ) {
				// loop through the relations, and if it doesn't exist, then clear it out
				let found_ministry = false;

				select?.relations[ state?.nation_select].forEach( option => {
					if ( option.value == state?.ministry_select ) {
						found_ministry = true;
					}
				});
				if ( ! found_ministry ) {
					updateStateAndResetPage( {ministry_select:null});
				}
				selectOptions = select?.relations[ state?.nation_select ];
			}
		});

		return selectOptions;
	}, [state?.nation_select]);
	return (
		<div className="location-list">
			<div className="location-list__container">
				<div className="location-list__aside">
					<div className="location-list__aside-image-wrapper">
						<div className="location-list__aside-image">
							{image && <Image {...image} />}
						</div>
					</div>
					<div className="location-list__aside-text">
						{heading && (
							<h3 className="location-list__aside-heading">
								{heading}
							</h3>
						)}
						{subheading && (
							<h4 className="location-list__aside-subheading">
								{subheading}
							</h4>
						)}
						{description && (
							<p className="location-list__aside-description">
								{description}
							</p>
						)}
						{linksHeading && sideBarList.length > 0 && (
							<h5 className="location-list__aside-links-heading">
								{linksHeading}
							</h5>
						)}
						{sideBarList && (
							<ul className="location-list__aside-items">
								{sideBarList.map((item, index) => (
									<li key={index} className="location-list__aside-item">
										<span>
											{item}
										</span>
									</li>
								))}
							</ul>
						)}
						{ministryBrochureLink && (
							<div>
								<a href={ministryBrochureLink}>View {heading} Ministry Brochures</a>
							</div>
						)}
						{ministryDescriptions[state.ministry_select] &&
							<div
								dangerouslySetInnerHTML={{
									__html: ministryDescriptions[state.ministry_select],
								}}
							/>
						}
					</div>
				</div>
				<div className="location-list__main" id='search_results_top'>
					<div className="location-list__program_code_selection">
						<p>
							Want to enter a donation code you received from your Intercede publication or other correspondence?<br /><br />
							<a className='btn btn-primary' target="_blank" href={byProgramCodeURL}>
								Enter a Donation Code
							</a>
						</p>
					</div>
					{intro && (
						<p className="location-list__intro">
							{intro}
						</p>
					)}
					{! records &&
						<Loading />
					}
					{records &&
						<>
						<LocationListFilter
							state={state}
							byProgramCodeURL={byProgramCodeURL}
							checkboxFilter={programCategories}
							selectFilter={filters}
							filteredMinistries={filteredMinistries}
							onCheck={ checkboxSelection => handleCheckboxSelection(checkboxSelection)}
							onSelect={ (filterName, filterSelection ) => handleFilterSelection( filterName, filterSelection)}
						/>
						{ busy &&
						<Loading />
						}
							{!busy &&
							<>
								{totalPages == 0 &&
								<div>No results found</div>
								}
								{totalPages > 0 &&
								<>
									<LocationListRecords records={records}/>
									{donationFormProps &&
									<ul className="location-list__results-items">
										{donationFormProps.map((donationForm, index) => (
											<DonationForm key={index} {...donationForm} />
										))}
									</ul>
									}
									<LocationListPagination
										className="location-list__pagination"
										page={state.currentPage}
										totalPages={totalPages}
										onChange={currentPage => updateState({currentPage: currentPage})}/>
								</>
								}
							</>
							}
						</>
					}
				</div>
			</div>
		</div>
	);
})

export default LocationList;
